import React from 'react'
import styled, { css } from 'styled-components'
import { Link, useGlobalLabels } from '@sentecacommerce-theme/lib'
import { useRouter } from 'next/router'
import { Typography, IconChevronRight as Arrow } from '../../../Common'

export const Pagination = (props: any) => {
  const router = useRouter()
  const { pagination: paginationLabels } = useGlobalLabels()
  const { pagination, slug } = props
  const pagesArray =
    pagination?.totalPages &&
    new Array(pagination?.totalPages).fill(1).map((_, i) => i + 1)
  const query: any = {}
  let arePrevDotsRendered = false
  let areNextDotsRendered = false

  if (router.query.q) {
    query.q = router.query.q
  } else {
    query.slug = slug
  }

  return (
    <>
      <Link
        href={{
          pathname: router.pathname,
          query: {
            ...query,
            ...pagination?.previousQuery,
          },
        }}
        passHref={pagination.hasPrevious}
      >
        <PaginationDirection
          prev
          canBeActivated={pagination.hasPrevious}
          aria-label={pagination.hasPrevious ? paginationLabels?.prevPage : undefined}
        >
          <Arrow />
        </PaginationDirection>
      </Link>
      {pagesArray
        ? pagesArray.map((el: number, index: any) => {
            if (el !== 1 && el < pagination?.currentPage - 1) {
              if (arePrevDotsRendered) return null

              arePrevDotsRendered = true

              return <Dots key={`Dots__${el}`}>...</Dots>
            }

            if (
              el !== pagination?.totalPages &&
              el > pagination?.currentPage + 2
            ) {
              if (areNextDotsRendered) return null

              areNextDotsRendered = true

              return <Dots key={`Dots__${el}${index}`}>...</Dots>
            }

            return (
              <Link
                key={`Link__${el}--${index}`}
                href={{
                  pathname: router.pathname || '',
                  query: {
                    ...((router.query as any) || {}),
                    page: el,
                  },
                }}
                passHref
              >
                <PageNumber current={el === pagination?.currentPage}>
                  <NumberContainer>{el}</NumberContainer>
                </PageNumber>
              </Link>
            )
          })
        : null}
      <Link
        href={{
          pathname: router.pathname,
          query: {
            ...query,
            ...pagination?.nextQuery,
          },
        }}
        passHref={pagination.hasNext}
      >
        <PaginationDirection
          canBeActivated={pagination.hasNext}
          aria-label={paginationLabels?.nextPage}
        >
          <Arrow />
        </PaginationDirection>
      </Link>
    </>
  )
}

type PaginationDirectionProps = {
  canBeActivated: boolean
  prev?: boolean
}
const PaginationDirection = styled.a.attrs(
  (props: PaginationDirectionProps) => ({
    as: props.canBeActivated ? 'a' : 'div',
    className: 'd-flex align-items-center justify-content-center',
  })
)<PaginationDirectionProps>`
  cursor: pointer;
  text-decoration: none;
  padding: 0.1rem 0.2rem 0.2rem;

  min-width: 3rem;
  min-height: 3rem;
  border-radius: 0.3rem;
  color: ${({ theme }) => theme.palette.greyTitle};
  border: 1px solid ${({ theme }) => theme.palette.greyTitle};

  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.lightgrey};
  }

  & > svg {
    position: relative;
    top: 0.1rem;
    opacity: 1;
    width: 1rem;
    height: 1rem;
  }

  ${(props) =>
    props.prev
      ? css`
          margin-right: 0.5rem;
          & svg {
            transform: rotate(-180deg);
          }
        `
      : css`
          margin-left: 0.5rem;
        `}

  ${(props) =>
    !props.canBeActivated &&
    css`
      pointer-events: none;
    `}
`

const PageNumber = styled(Typography).attrs({
  color: 'primary',
  variant: 7,
  as: 'a',
})<{ current: boolean }>`
  position: relative;
  color: ${({ theme }) => theme.palette.greyTitle};
  min-width: 3rem;
  min-height: 3rem;
  border-radius: 0.3rem;
  cursor: pointer;
  margin: 0 0.25rem;

  &:hover {
    border: 1px solid ${({ theme }) => theme.palette.greyTitle};
    background-color: ${({ theme }) => theme.palette.lightgrey};
  }

  ${(props) =>
    props.current &&
    css`
      border: 1px solid ${({ theme }) => theme.palette.greyTitle};
      background-color: ${({ theme }) => theme.palette.lightgrey};
    `}
  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    margin: 0 0.5rem;
  }
`
const Dots = styled.div.attrs({
  className: 'd-md-block',
})`
  pointer-events: none;
  color: ${({ theme }) => theme.palette.greyTitle};
  font-weight: 700;
`
const NumberContainer = styled.div.attrs({
  className: '',
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
